import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import {
  AccountApi,
  AccountInformationResponse,
  ApiErrorResponse,
  AuthenticationResponse,
  Configuration,
  GetSignupResponse,
  LoginEndResponse,
  ResetPasswordRequestBody,
  SignupRequestBodyAcceptHealthDataProcessingEnum,
  SignupResponse,
  UpdateEmailRequestBody,
  UpdatePasswordRequestBody,
  VerifyEmailResponseBody,
} from 'oura-account-api-client';

import { SignupInfo } from '../store';
import { AccountApiError } from '../utils/AccountApiError';
import { logger } from '../utils/logger';

const API_URL = import.meta.env.VITE_ACCOUNT_API_URL;
const api = new AccountApi(new Configuration({ basePath: API_URL }));

function handleApiError(error: unknown): never {
  if (axios.isAxiosError<ApiErrorResponse>(error) && error.response?.data) {
    if (error.response?.data) {
      const {
        response: {
          data: { errorCode, message, detail },
        },
      } = error;
      throw new AccountApiError(message, errorCode, detail);
    }
  }
  throw error;
}

const getSignup = async (token: string) => {
  const reqBody = { token };
  const response = await api.getSignup(reqBody).catch(handleApiError);
  return response.data;
};

/** Getting email by signup token */
export const useGetSignup = (token: string) =>
  useQuery<GetSignupResponse>({
    queryKey: ['getSignup', token],
    queryFn: () => getSignup(token),
    retry: false,
    staleTime: Infinity,
    enabled: !!token,
  });

interface CreateUserPayload {
  password: string;
  userAgreement: SignupRequestBodyAcceptHealthDataProcessingEnum;
  marketingAgreement: boolean;
  signupInfo: SignupInfo;
}

export const createUser = async ({
  password,
  userAgreement,
  signupInfo,
}: CreateUserPayload): Promise<SignupResponse> => {
  const { termsOfServiceVersion, privacyPolicyVersion, token } = signupInfo;
  const reqBody = {
    signupRequestBody: {
      password: password,
      acceptHealthDataProcessing: userAgreement,
      termsOfServiceVersion,
      privacyPolicyVersion,
      token,
    },
  };
  const resp = await api.postSignup(reqBody, { withCredentials: true }).catch(handleApiError);

  logger.setContextProperty('accountId', resp.data.accountId);
  logger.setContextProperty('analyticsId', resp.data.analyticsId);
  return resp.data;
};

export const validateJwt = async (jwtToken: string): Promise<void> => {
  await api
    .postJwtAccess({
      headers: { Authorization: 'Bearer ' + jwtToken },
    })
    .catch(handleApiError);

  return;
};

export const refreshTokens = async (): Promise<AuthenticationResponse> => {
  const response = await api
    .postJwtRefresh({
      withCredentials: true,
    })
    .catch(handleApiError);

  logger.setContextProperty('accountId', response.data.accountId);
  logger.setContextProperty('analyticsId', response.data.analyticsId);
  return response.data;
};

export const updatePassword = async ({ password, newPassword }: UpdatePasswordRequestBody) => {
  const reqBody = { updatePasswordRequestBody: { password, newPassword } };
  const response = await api
    .updatePassword(reqBody, {
      withCredentials: true,
    })
    .catch(handleApiError);
  return response.status;
};

export const updateEmail = async ({ password, email }: UpdateEmailRequestBody) => {
  const reqBody = { updateEmailRequestBody: { password, email } };
  const response = await api.updateEmail(reqBody, { withCredentials: true }).catch(handleApiError);
  return response.status;
};

const verifyEmail = async (token: string) => {
  const reqBody = {
    verifyEmailRequestBody: { token },
  };
  const response = await api.verifyEmail(reqBody).catch(handleApiError);
  return response.data;
};

export const useVerifyNewEmail = (token: string) =>
  useQuery<VerifyEmailResponseBody>({
    queryKey: ['verifyEmail', token],
    queryFn: () => verifyEmail(token),
    retry: false,
    staleTime: Infinity,
    enabled: !!token,
  });

export const sendResetPasswordRequest = async (email: string) => {
  const reqBody = {
    requestResetPasswordBody: { email },
  };
  await api.requestResetPassword(reqBody).catch(handleApiError);
};

export const resetPassword = async ({ token, password }: ResetPasswordRequestBody) => {
  const reqBody = {
    resetPasswordRequestBody: { token, password },
  };
  const response = await api.resetPassword(reqBody).catch(handleApiError);

  logger.setContextProperty('accountId', response.data.accountId);
  return response.data;
};

export const logoutUser = async (moiRedirectUrl?: string) => {
  const logoutResponse = await api
    .postAccountLogout(
      {
        logoutRequestBody: {
          redirectUrl: import.meta.env.VITE_URL + '/login/?next=/' + moiRedirectUrl,
        },
      },
      { withCredentials: true },
    )
    .catch(handleApiError);
  return logoutResponse.data;
};
export interface InitLoginResponse {
  authorizationRequestUrl: string;
}

/** MOI queries */

/*
 * Initiates MOI login process by generating a temporary cookie and redirects the user to MOI login page where user is asked to input email address and one-time code from email
 */
export const useInitLogin = (
  redirectUri: string,
  enabled: boolean,
  nonce?: string,
): UseQueryResult<InitLoginResponse> =>
  useQuery({
    queryKey: ['initLogin', redirectUri, nonce],
    queryFn: async (): Promise<InitLoginResponse> => {
      const response = await api.initiateLogin(
        { initLoginRequest: { redirectUri, nonce } },
        {
          withCredentials: true,
        },
      );
      return response.data;
    },
    enabled,
  });

/*
 * Completes MOI login process by exchanging the code and state for an access token
 */
export const useCompleteLogin = (
  redirectUri: string,
  code: string,
  state: string,
  enabled: boolean,
): UseQueryResult<LoginEndResponse> =>
  useQuery({
    queryKey: ['completeLogin', redirectUri, code, state],
    queryFn: async (): Promise<LoginEndResponse> => {
      const response = await api.endLogin({ loginEndRequest: { code, state, redirectUri } }, { withCredentials: true });

      logger.setContextProperty('accountId', response.data.accountId);
      logger.setContextProperty('analyticsId', response.data.analyticsId);

      return response.data;
    },
    enabled,
  });

export const useGetAccountInfo = (enabled = true): UseQueryResult<AccountInformationResponse> =>
  useQuery({
    queryKey: ['accountInfo'],
    queryFn: async (): Promise<AccountInformationResponse> => {
      const response = await api.accountMe({ withCredentials: true }).catch(handleApiError);
      return response.data;
    },
    enabled,
  });
